import { useMemo, useRef, useState, type ChangeEvent } from "react";
import { Alert, Button, Form, Input, Space, Switch } from "antd";
import type { FormInstance } from "antd";
import { QRCodeCanvas } from "qrcode.react";
import { getApiErrorMessage, type ApiError } from "../utils/apiError";

export const POSITION_PATTERN = /^[0-9][A-Z][0-9]{2}$/;

export type PositionLabelSubmitHandler = (
  position: string,
  includeArrow: boolean,
  printerPort: number
) => Promise<void>;

export type PositionLabelFormValues = {
  position: string;
  includeArrow: boolean;
};

type PositionLabelSectionProps = {
  onSubmit: PositionLabelSubmitHandler;
  printerPort: number;
  /** Lets the page put a scanned position in, keeping "So šípkou" as it is. */
  form?: FormInstance<PositionLabelFormValues>;
};

const PositionLabelSection = ({ onSubmit, printerPort, form: formProp }: PositionLabelSectionProps) => {
  const [form] = Form.useForm<PositionLabelFormValues>(formProp);
  // Shown only while the field still holds the position it is about: a scanned position put in by
  // the page must not sit next to "odoslaný" from the label before it.
  const [status, setStatus] = useState<
    { type: "success" | "error"; message: string; position: string } | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const clickLockRef = useRef(false);

  const positionValue = Form.useWatch("position", form) ?? "";
  const includeArrowValue = Form.useWatch("includeArrow", form) ?? false;
  const normalizedPosition = useMemo(
    () => positionValue.trim().toUpperCase(),
    [positionValue]
  );
  const isPositionValid = POSITION_PATTERN.test(normalizedPosition);

  const handleFinish = async (values: PositionLabelFormValues) => {
    const normalizedPosition = values.position.trim().toUpperCase();
    const includeArrow =
      form.getFieldValue("includeArrow") ?? values.includeArrow;
    setStatus(null);
    setIsSubmitting(true);

    try {
      await onSubmit(normalizedPosition, includeArrow, printerPort);
      form.setFieldsValue({ position: "" });
      form.resetFields(["position"]);
      setStatus({
        type: "success",
        message: "Štítok odoslaný na tlačiareň",
        position: "",
      });
    } catch (error) {
      const message = getApiErrorMessage(error as ApiError);
      setStatus({ type: "error", message, position: form.getFieldValue("position") ?? "" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLargeLabelPrint = () => {
    const currentValue = (form.getFieldValue("position") ?? "") as string;
    const normalized = currentValue.trim().toUpperCase();

    if (!POSITION_PATTERN.test(normalized)) {
      void form.validateFields(["position"]);
      return;
    }

    if (clickLockRef.current) {
      return;
    }

    clickLockRef.current = true;
    window.setTimeout(() => {
      clickLockRef.current = false;
    }, 800);

    let qrUrl: string | null = null;
    try {
      const canvas = qrCanvasRef.current;
      if (canvas && typeof canvas.toDataURL === "function") {
        qrUrl = canvas.toDataURL("image/png");
      }
    } catch (error) {
      console.warn("Unable to export QR code", error);
    }

    const line1 = normalized.slice(0, 2);
    const line2 = normalized.slice(2, 4);
    const qrImage = qrUrl ? `<img src="${qrUrl}" alt="QR ${normalized}@" />` : "";
    const html = `<!doctype html><html><head><meta charset="utf-8" />
<style>
  @page { size: landscape; margin: 10mm; }
  html, body { margin:0; padding:0; width:100%; height:100%; }
  *, *::before, *::after { box-sizing: border-box; }
  body, .page { page-break-inside: avoid; break-inside: avoid; overflow: hidden; }

  body { display:flex; align-items:center; justify-content:center; }
  .page { width: 85%; height: 95%; display: grid; grid-template-columns: 1fr 1fr; align-items: center; justify-items: stretch; }
  .left, .right { width:100%; height:100%; display:flex; align-items:center; padding: 0 4%; }
  .left { justify-content:flex-start; }
  .right { justify-content:flex-end; }

  .left img { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; }

  .code { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-weight: 800; line-height: 1; text-align:right; display:flex; flex-direction:column; align-items:flex-end; justify-content:center; width:100%; height:100%; }
  .line { font-size: clamp(30mm, 38vmin, 180mm); letter-spacing: 0.025em; }

  @media print { .screen-only { display:none !important; } }
</style>
<title>${normalized}</title></head><body>
  <div class="page">
    <div class="left">${qrImage}</div>
    <div class="right">
      <div class="code">
        <div class="line">${line1}</div>
        <div class="line">${line2}</div>
      </div>
    </div>
  </div>
  <script>
    window.addEventListener('load', function(){
      try { window.focus(); } catch (e) {}
      setTimeout(function(){ try { window.print(); } catch (e) {} }, 50);
    });
  </script>
</body></html>`;

    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      return;
    }

    try {
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } catch (error) {
      console.warn("Unable to render print window", error);
    }
  };

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        size="large"
        initialValues={{ position: "", includeArrow: false }}
        onFinish={handleFinish}
        onValuesChange={() => {
          if (status) {
            setStatus(null);
          }
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {status && status.position === positionValue ? (
            <Alert type={status.type} message={status.message} />
          ) : null}

          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <Form.Item
              name="position"
              style={{ flex: 1, marginBottom: 0 }}
              rules={[
                {
                  required: true,
                  message: "Zadajte pozíciu skladu (napr. 1A14)",
                },
                {
                  pattern: POSITION_PATTERN,
                  message:
                    "Neplatná pozícia. Očakávaný formát: 1A14 (číslica 0-9, veľké písmeno, dve číslice)",
                },
              ]}
              getValueFromEvent={(event: ChangeEvent<HTMLInputElement>) =>
                event.target.value.toUpperCase()
              }
            >
              <Input
                placeholder="Zadajte pozíciu (napr. 1A14)"
                maxLength={4}
                autoComplete="off"
              />
            </Form.Item>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Form.Item
                name="includeArrow"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
                noStyle
              >
                <Switch />
              </Form.Item>
              <span>{includeArrowValue ? "So šípkou" : "Bez šípky"}</span>
            </div>
          </div>

          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Button
              block
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
            >
              Vytlač štítok pozície
            </Button>

            <Button
              block
              onClick={handleLargeLabelPrint}
              disabled={!isPositionValid}
            >
              Otvoriť veľký štítok (A4/A3/A5)
            </Button>
          </Space>
        </Space>
      </Form>
      {/* Only the source of the big label's image; off-screen positioning made the page 2048 px tall. */}
      <div style={{ display: "none" }}>
        <QRCodeCanvas
          ref={qrCanvasRef as any}
          value={normalizedPosition ? `${normalizedPosition}@` : ""}
          size={2048}
          includeMargin={false}
          level="H"
        />
      </div>
    </div>
  );
};

export default PositionLabelSection;
